// src/components/AppointmentsPage.js — إدارة المواعيد (حجوزات / موارد ومواعيد عمل / تذكيرات)
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import {
  Box, Card, Typography, Tabs, Tab, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Button, IconButton, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, CircularProgress, Tooltip, Switch,
  FormControlLabel,
} from '@mui/material';
import PageHeader from './common/PageHeader';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';

const T = { ink: '#0f172a', muted: '#64748b', faint: '#94a3b8', border: '#e9eef5' };

const BOOKING_STATUS = {
  PENDING: { label: 'بانتظار التأكيد', bg: '#fef3d9', fg: '#b45309' },
  CONFIRMED: { label: 'مؤكد', bg: '#e4faf1', fg: '#047857' },
  CANCELLED: { label: 'ملغي', bg: '#ffe9ec', fg: '#be123c' },
  COMPLETED: { label: 'تم', bg: '#e6f6fe', fg: '#0369a1' },
  NO_SHOW: { label: 'لم يحضر', bg: '#eef2f7', fg: '#334155' },
};
const REMINDER_STATUS = {
  PENDING: { label: 'مجدول', bg: '#e6f6fe', fg: '#0369a1' },
  SENT: { label: 'أُرسل', bg: '#e4faf1', fg: '#047857' },
  FAILED: { label: 'فشل', bg: '#ffe9ec', fg: '#be123c' },
  CANCELLED: { label: 'ملغي', bg: '#eef2f7', fg: '#334155' },
  SKIPPED: { label: 'تخطّي', bg: '#eef2f7', fg: '#334155' },
};
const WEEKDAYS = ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'];

const StatusChip = ({ map, value }) => {
  const m = map[value] || { label: value, bg: '#eef2f7', fg: '#334155' };
  return <Chip label={m.label} size="small" sx={{ bgcolor: m.bg, color: m.fg, fontWeight: 700 }} />;
};

const fmtDT = (v) =>
  v ? new Date(v).toLocaleString('ar-EG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const Panel = ({ children }) => (
  <Card elevation={0} sx={{ overflow: 'hidden' }}>{children}</Card>
);

const EmptyState = ({ icon, title, hint }) => (
  <Box sx={{ py: 7, textAlign: 'center' }}>
    <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#f4f7fb', display: 'grid', placeItems: 'center', mx: 'auto', mb: 1.5, color: T.faint }}>
      {icon}
    </Box>
    <Typography sx={{ color: T.muted, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ color: T.faint, fontSize: '0.82rem', mt: 0.5 }}>{hint}</Typography>
  </Box>
);

export default function AppointmentsPage() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [resources, setResources] = useState([]);
  const [rules, setRules] = useState([]);
  const [reminders, setReminders] = useState([]);

  const [resourceDialog, setResourceDialog] = useState(false);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [resourceForm, setResourceForm] = useState({ name: '', resource_type: 'doctor', capacity: 1 });
  const [ruleForm, setRuleForm] = useState({
    weekday: 0, start_time: '09:00', end_time: '17:00', slot_duration_minutes: 30, is_blackout: false, resource: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    const [b, r, a, m] = await Promise.allSettled([
      axiosInstance.get('/chatbot/bookings/'),
      axiosInstance.get('/chatbot/resources/'),
      axiosInstance.get('/chatbot/availability-rules/'),
      axiosInstance.get('/chatbot/reminders/'),
    ]);
    if (b.status === 'fulfilled') setBookings(b.value.data.results || b.value.data);
    if (r.status === 'fulfilled') setResources(r.value.data.results || r.value.data);
    if (a.status === 'fulfilled') setRules(a.value.data.results || a.value.data);
    if (m.status === 'fulfilled') setReminders(m.value.data.results || m.value.data);
    if ([b, r, a, m].every((x) => x.status === 'rejected')) setError('فشل في تحميل بيانات المواعيد.');
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setBookingStatus = async (id, status) => {
    try {
      const res = await axiosInstance.post(`/chatbot/bookings/${id}/status/`, { status });
      setBookings((prev) => prev.map((x) => (x.id === id ? res.data : x)));
    } catch { alert('تعذّر تحديث حالة الحجز'); }
  };

  const addResource = async () => {
    try {
      const res = await axiosInstance.post('/chatbot/resources/', resourceForm);
      setResources((prev) => [...prev, res.data]);
      setResourceDialog(false);
      setResourceForm({ name: '', resource_type: 'doctor', capacity: 1 });
    } catch { alert('تعذّر إضافة المورد'); }
  };

  const deleteResource = async (id) => {
    if (!window.confirm('حذف هذا المورد؟')) return;
    try {
      await axiosInstance.delete(`/chatbot/resources/${id}/`);
      setResources((prev) => prev.filter((x) => x.id !== id));
    } catch { alert('تعذّر الحذف'); }
  };

  const addRule = async () => {
    try {
      const payload = { ...ruleForm, resource: ruleForm.resource || null };
      const res = await axiosInstance.post('/chatbot/availability-rules/', payload);
      setRules((prev) => [...prev, res.data]);
      setRuleDialog(false);
    } catch { alert('تعذّر إضافة القاعدة — تأكد من صحة الأوقات'); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('حذف قاعدة التوفّر؟')) return;
    try {
      await axiosInstance.delete(`/chatbot/availability-rules/${id}/`);
      setRules((prev) => prev.filter((x) => x.id !== id));
    } catch { alert('تعذّر الحذف'); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#0ea5e9' }} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="المواعيد والحجوزات"
        subtitle="إدارة حجوزات العملاء، مواعيد العمل، والتذكيرات الآلية"
        onRefresh={fetchAll}
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}

      <Card elevation={0} sx={{ mb: 3, px: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ '& .MuiTab-root': { fontWeight: 700 } }}>
          <Tab label={`الحجوزات (${bookings.length})`} />
          <Tab label="الموارد ومواعيد العمل" />
          <Tab label={`التذكيرات (${reminders.length})`} />
        </Tabs>
      </Card>

      {/* ============ الحجوزات ============ */}
      {tab === 0 && (
        <Panel>
          {bookings.length === 0 ? (
            <EmptyState
              icon={<EventAvailableRoundedIcon sx={{ fontSize: 30 }} />}
              title="لا توجد حجوزات بعد"
              hint="عندما يحجز عملاؤك عبر المساعد الذكي ستظهر الحجوزات هنا"
            />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>العميل</TableCell>
                  <TableCell>الموعد</TableCell>
                  <TableCell>المورد</TableCell>
                  <TableCell>الأشخاص</TableCell>
                  <TableCell>التذكيرات</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell align="center">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell sx={{ fontWeight: 700, direction: 'ltr', textAlign: 'right' }}>{b.customer_phone}</TableCell>
                    <TableCell>{fmtDT(b.start_at)}</TableCell>
                    <TableCell>{b.resource_name || '—'}</TableCell>
                    <TableCell>{b.party_size || '—'}</TableCell>
                    <TableCell>
                      <Chip icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 14 }} />} label={b.reminders_total}
                        size="small" sx={{ bgcolor: '#e6f6fe', color: '#0369a1', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell><StatusChip map={BOOKING_STATUS} value={b.status} /></TableCell>
                    <TableCell align="center">
                      {['PENDING'].includes(b.status) && (
                        <Tooltip title="تأكيد">
                          <IconButton size="small" sx={{ color: '#047857' }} onClick={() => setBookingStatus(b.id, 'CONFIRMED')}>
                            <CheckCircleOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {['PENDING', 'CONFIRMED'].includes(b.status) && (
                        <Tooltip title="إلغاء (يلغي التذكيرات)">
                          <IconButton size="small" sx={{ color: '#be123c' }} onClick={() => setBookingStatus(b.id, 'CANCELLED')}>
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>
      )}

      {/* ============ الموارد ومواعيد العمل ============ */}
      {tab === 1 && (
        <Stack spacing={3}>
          <Panel>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 1 }}>
              <Typography sx={{ fontWeight: 800, color: T.ink }}>الموارد القابلة للحجز</Typography>
              <Button startIcon={<AddRoundedIcon />} variant="contained" size="small" onClick={() => setResourceDialog(true)}
                sx={{ '& .MuiButton-startIcon': { ml: 0.5, mr: -0.25 } }}>
                إضافة مورد
              </Button>
            </Stack>
            {resources.length === 0 ? (
              <EmptyState icon={<AddRoundedIcon sx={{ fontSize: 30 }} />} title="لا توجد موارد"
                hint="أضف طبيباً أو طاولة أو غرفة… أي شيء يُحجز عليه" />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>الاسم</TableCell><TableCell>النوع</TableCell>
                    <TableCell>السعة</TableCell><TableCell align="center">حذف</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resources.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{r.name}</TableCell>
                      <TableCell><Chip label={r.resource_type} size="small" sx={{ bgcolor: '#efecfe', color: '#5b45d6', fontWeight: 700 }} /></TableCell>
                      <TableCell>{r.capacity}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" sx={{ color: T.faint }} onClick={() => deleteResource(r.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Panel>

          <Panel>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, color: T.ink }}>مواعيد العمل</Typography>
                <Typography sx={{ color: T.faint, fontSize: '0.78rem' }}>
                  بدون قواعد: يقبل البوت أي وقت. أضف قواعد ليتم رفض الأوقات خارج الدوام تلقائياً.
                </Typography>
              </Box>
              <Button startIcon={<AddRoundedIcon />} variant="outlined" size="small" onClick={() => setRuleDialog(true)}
                sx={{ '& .MuiButton-startIcon': { ml: 0.5, mr: -0.25 } }}>
                إضافة قاعدة
              </Button>
            </Stack>
            {rules.length === 0 ? (
              <EmptyState icon={<EventAvailableRoundedIcon sx={{ fontSize: 30 }} />} title="لا توجد قواعد دوام"
                hint="مثال: الأحد–الخميس من 9 صباحاً حتى 5 مساءً" />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>اليوم</TableCell><TableCell>من</TableCell><TableCell>إلى</TableCell>
                    <TableCell>مدة الموعد</TableCell><TableCell>النوع</TableCell><TableCell align="center">حذف</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{r.date || WEEKDAYS[r.weekday] || '—'}</TableCell>
                      <TableCell>{r.start_time?.slice(0, 5)}</TableCell>
                      <TableCell>{r.end_time?.slice(0, 5)}</TableCell>
                      <TableCell>{r.slot_duration_minutes} د</TableCell>
                      <TableCell>
                        {r.is_blackout
                          ? <Chip label="إغلاق" size="small" sx={{ bgcolor: '#ffe9ec', color: '#be123c', fontWeight: 700 }} />
                          : <Chip label="دوام" size="small" sx={{ bgcolor: '#e4faf1', color: '#047857', fontWeight: 700 }} />}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" sx={{ color: T.faint }} onClick={() => deleteRule(r.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Panel>
        </Stack>
      )}

      {/* ============ التذكيرات ============ */}
      {tab === 2 && (
        <Panel>
          {reminders.length === 0 ? (
            <EmptyState icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 30 }} />} title="لا توجد تذكيرات"
              hint="تُجدول التذكيرات تلقائياً عند إنشاء حجز جديد" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>العميل</TableCell><TableCell>موعد الحجز</TableCell>
                  <TableCell>وقت الإرسال</TableCell><TableCell>القالب</TableCell>
                  <TableCell>محاولات</TableCell><TableCell>الحالة</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reminders.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 700, direction: 'ltr', textAlign: 'right' }}>{r.customer_phone}</TableCell>
                    <TableCell>{fmtDT(r.booking_start_at)}</TableCell>
                    <TableCell>{fmtDT(r.send_at)}</TableCell>
                    <TableCell><Chip label={r.template_name} size="small" sx={{ bgcolor: '#f4f7fb', color: T.muted, fontWeight: 700 }} /></TableCell>
                    <TableCell>{r.attempts}</TableCell>
                    <TableCell><StatusChip map={REMINDER_STATUS} value={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>
      )}

      {/* ============ Dialog: مورد جديد ============ */}
      <Dialog open={resourceDialog} onClose={() => setResourceDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>إضافة مورد جديد</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="الاسم" fullWidth value={resourceForm.name}
              onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })} />
            <TextField label="النوع" select fullWidth value={resourceForm.resource_type}
              onChange={(e) => setResourceForm({ ...resourceForm, resource_type: e.target.value })}>
              {['doctor', 'table', 'room', 'other'].map((t) => (
                <MenuItem key={t} value={t}>{{ doctor: 'طبيب', table: 'طاولة', room: 'غرفة', other: 'أخرى' }[t]}</MenuItem>
              ))}
            </TextField>
            <TextField label="السعة" type="number" fullWidth value={resourceForm.capacity}
              onChange={(e) => setResourceForm({ ...resourceForm, capacity: Number(e.target.value) || 1 })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResourceDialog(false)}>إلغاء</Button>
          <Button variant="contained" onClick={addResource} disabled={!resourceForm.name.trim()}>إضافة</Button>
        </DialogActions>
      </Dialog>

      {/* ============ Dialog: قاعدة دوام ============ */}
      <Dialog open={ruleDialog} onClose={() => setRuleDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>إضافة قاعدة دوام</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="اليوم" select fullWidth value={ruleForm.weekday}
              onChange={(e) => setRuleForm({ ...ruleForm, weekday: Number(e.target.value) })}>
              {WEEKDAYS.map((d, i) => <MenuItem key={i} value={i}>{d}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="من" type="time" fullWidth value={ruleForm.start_time}
                onChange={(e) => setRuleForm({ ...ruleForm, start_time: e.target.value })} />
              <TextField label="إلى" type="time" fullWidth value={ruleForm.end_time}
                onChange={(e) => setRuleForm({ ...ruleForm, end_time: e.target.value })} />
            </Stack>
            <TextField label="مدة الموعد (دقائق)" type="number" fullWidth value={ruleForm.slot_duration_minutes}
              onChange={(e) => setRuleForm({ ...ruleForm, slot_duration_minutes: Number(e.target.value) || 30 })} />
            <FormControlLabel
              control={<Switch checked={ruleForm.is_blackout}
                onChange={(e) => setRuleForm({ ...ruleForm, is_blackout: e.target.checked })} />}
              label="فترة إغلاق (بدلاً من دوام)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRuleDialog(false)}>إلغاء</Button>
          <Button variant="contained" onClick={addRule}>إضافة</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
